import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getAuthToken } from '../utils/auth';
import Logo from './Logo';
// Helper function to format assistant messages with enhanced styling
const formatAssistantMessage = (text) => {
    return text
        // Bold headers with icons
        .replace(/\*\*([^*]+):\*\*/g, '<div class="fw-bold text-primary mb-2 mt-3" style="font-size: 16px;"><svg class="sharpshot-logo me-2" width="16" height="16" viewBox="0 0 100 100" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M50 5 L85 75 L65 55 L50 40 L35 55 L15 75 Z" /></svg>$1:</div>')
        // Bullet points with enhanced styling
        .replace(/^• (.+)$/gm, '<div class="ms-3 mb-2" style="font-size: 15px;"><i class="fas fa-circle text-success me-2" style="font-size: 8px;"></i>$1</div>')
        // Team names clickable (basic version)
        .replace(/\b([A-Z][a-z]+ [A-Z][a-z]+|Yankees|Astros|Braves|Mets|Pirates|Phillies|Angels|Giants|Mariners|Padres|Nationals|Blue Jays|White Sox|Reds)\b/g, '<span class="text-primary fw-bold" style="cursor: pointer; text-decoration: underline;">$1</span>')
        // Confidence levels with colors
        .replace(/\b(HIGH|MEDIUM_PLUS|MEDIUM|LOW)\b/g, (match) => {
        const colors = {
            'HIGH': 'success',
            'MEDIUM_PLUS': 'info',
            'MEDIUM': 'warning',
            'LOW': 'secondary'
        };
        return `<span class="badge bg-${colors[match]} me-1" style="font-size: 13px; padding: 6px 10px;">${match.replace('_', '-')}</span>`;
    })
        // ROI percentages with colors
        .replace(/([+-]?\d+\.?\d*)%\s*(ROI|Expected ROI)/g, (_, percentage, type) => {
        const num = parseFloat(percentage);
        const colorClass = num > 0 ? 'text-success' : num < 0 ? 'text-danger' : 'text-muted';
        return `<span class="${colorClass} fw-bold" style="font-size: 15px;">${percentage}%</span> ${type}`;
    })
        // Line breaks for better formatting
        .replace(/\n/g, '<br>');
};
const ChatWidget = ({ pageContext, isOpen: externalIsOpen, onToggle }) => {
    const { user } = useAuth();
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    // Use external state if provided, otherwise use internal state
    const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
    const toggleChat = onToggle || (() => setInternalIsOpen(!internalIsOpen));
    const [messages, setMessages] = useState([]);
    const [inputText, setInputText] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef(null);
    const [showQuickActions, setShowQuickActions] = useState(true);
    // Only show for Elite subscribers
    const isEliteUser = user?.subscriptionTier === 'elite';
    // Auto-scroll to bottom when new messages are added
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);
    // Initial greeting when chat opens
    useEffect(() => {
        if (isOpen && messages.length === 0) {
            const greeting = getContextualGreeting(pageContext);
            setMessages([{
                    id: '1',
                    text: greeting,
                    sender: 'assistant',
                    timestamp: new Date()
                }]);
        }
    }, [isOpen, pageContext, messages.length]);
    const getContextualGreeting = (context) => {
        const baseGreeting = "Hi! I'm your SharpShot AI assistant. I can help explain our betting strategies, model predictions, and answer questions about our platform.";
        switch (context) {
            case 'game-analysis':
                return `${baseGreeting} I see you're looking at game analysis - I can explain why we made specific picks and what our models considered.`;
            case 'performance':
                return `${baseGreeting} I can help you understand the performance metrics and what they mean for your betting strategy.`;
            case 'my-bets':
                return `${baseGreeting} I can help explain your prediction history and provide insights on betting patterns.`;
            default:
                return baseGreeting;
        }
    };
    const quickActions = [
        { text: "🎯 Today's High Confidence Games", query: "Show me today's high confidence games" },
        { text: "⚠️ Games to Avoid", query: "What games should I avoid today?" },
        { text: "📊 My Betting Performance", query: "How is my betting performance?" },
        { text: "💎 Best Value Bets", query: "What are the best value bets available now?" }
    ];
    const handleQuickAction = (query) => {
        setInputText(query);
        setShowQuickActions(false);
        // Auto-send the message
        setTimeout(() => {
            sendMessage();
        }, 100);
    };
    const sendMessage = async () => {
        if (!inputText.trim() || isLoading)
            return;
        console.log('ChatWidget: Starting sendMessage');
        const userMessage = {
            id: Date.now().toString(),
            text: inputText,
            sender: 'user',
            timestamp: new Date()
        };
        setMessages(prev => [...prev, userMessage]);
        setInputText('');
        setIsLoading(true);
        try {
            const token = getAuthToken();
            console.log('Chat token:', token ? token.substring(0, 20) + '...' : 'null');
            console.log('Full token length:', token ? token.length : 0);
            if (!token) {
                throw new Error('No token found in localStorage');
            }
            const response = await fetch('/api/chat', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    message: inputText,
                    pageContext: pageContext,
                    chatHistory: messages.slice(-5) // Send last 5 messages for context
                }),
            });
            if (!response.ok) {
                throw new Error('Failed to get response');
            }
            const data = await response.json();
            const assistantMessage = {
                id: (Date.now() + 1).toString(),
                text: data.message,
                sender: 'assistant',
                timestamp: new Date()
            };
            setMessages(prev => [...prev, assistantMessage]);
        }
        catch (error) {
            console.error('Chat error:', error);
            const errorMessage = {
                id: (Date.now() + 1).toString(),
                text: "I'm sorry, I'm having trouble connecting right now. Please try again in a moment or contact support at sharpshot.help@gmail.com",
                sender: 'assistant',
                timestamp: new Date()
            };
            setMessages(prev => [...prev, errorMessage]);
        }
        finally {
            setIsLoading(false);
        }
    };
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };
    const clearChat = () => {
        setMessages([]);
    };
    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            // Could add a toast notification here
        }
        catch (err) {
            console.error('Failed to copy text: ', err);
        }
    };
    // Don't render if not Elite user
    if (!isEliteUser) {
        return null;
    }
    return (_jsxs(_Fragment, { children: [_jsx("button", { onClick: toggleChat, className: `chat-toggle-btn ${isOpen ? 'bg-danger' : 'bg-primary'}`, style: {
                    color: 'white'
                }, title: isOpen ? 'Close Chat' : 'Open AI Assistant', "aria-label": isOpen ? 'Close Chat' : 'Open AI Assistant', type: "button", role: "button", children: isOpen ? (_jsx("i", { className: "fas fa-times fa-lg" })) : (_jsx(Logo, { width: 24, height: 24, style: {
                        color: '#ffd700',
                        filter: 'drop-shadow(0 2px 4px rgba(255,215,0,0.5))'
                    } })) }), isOpen && (_jsxs("div", { className: "position-fixed rounded shadow-lg border-0 chat-widget", style: {
                    zIndex: 999,
                    display: 'flex',
                    flexDirection: 'column',
                    background: 'var(--bg-tertiary)',
                    boxShadow: 'var(--shadow-lg)',
                    border: '1px solid var(--border-secondary)'
                }, children: [_jsxs("div", { className: "d-flex justify-content-between align-items-center p-4 border-bottom rounded-top", style: {
                            background: 'var(--accent-blue)',
                            color: 'var(--text-inverse)',
                            minHeight: '80px',
                            borderBottom: '1px solid var(--border-secondary)'
                        }, children: [_jsxs("div", { className: "d-flex align-items-center", children: [_jsx("div", { className: "d-flex align-items-center justify-content-center me-3 rounded-circle", style: {
                                            width: '45px',
                                            height: '45px',
                                            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.8)',
                                            border: '2px solid rgba(255,255,255,0.3)'
                                        }, children: _jsxs("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [_jsx("path", { d: "M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1H5C3.89 1 3 1.89 3 3V21C3 22.11 3.89 23 5 23H11.81C12.45 23 13.06 22.69 13.44 22.19L21.83 11.83C22.59 10.79 22.16 9.29 21 9ZM7 7H10V9H7V7ZM7 11H10V13H7V11ZM7 15H9V17H7V15Z", fill: "#007bff" }), _jsx("circle", { cx: "17", cy: "17", r: "5", fill: "#28a745" }), _jsx("path", { d: "M15 17L16.5 18.5L19 16", stroke: "white", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" })] }) }), _jsxs("div", { children: [_jsx("div", { className: "fw-bold fs-5", children: "SharpShot AI" }), _jsx("small", { className: "opacity-75 d-block", children: "Your Personal Betting Analyst" })] })] }), _jsx("div", { className: "d-flex align-items-center", children: _jsx("button", { onClick: clearChat, className: "btn btn-sm btn-link text-white p-0", title: "Clear Chat", children: _jsx("i", { className: "fas fa-trash fa-sm" }) }) })] }), _jsxs("div", { className: "flex-grow-1 p-4 overflow-auto", style: {
                            maxHeight: '500px',
                            background: 'var(--bg-primary)',
                            scrollbarWidth: 'thin',
                            scrollbarColor: 'var(--border-primary) transparent'
                        }, children: [messages.map((message) => (_jsx("div", { className: `mb-3 d-flex ${message.sender === 'user' ? 'justify-content-end' : 'justify-content-start'}`, children: _jsxs("div", { className: `rounded-3 p-3 ${message.sender === 'user'
                                        ? 'text-white'
                                        : 'text-dark'}`, style: {
                                        maxWidth: '90%',
                                        fontSize: '15px',
                                        lineHeight: '1.5',
                                        background: message.sender === 'user'
                                            ? 'var(--accent-blue)'
                                            : 'var(--bg-tertiary)',
                                        color: message.sender === 'user'
                                            ? 'var(--text-inverse)'
                                            : 'var(--text-primary)',
                                        boxShadow: message.sender === 'user'
                                            ? 'var(--shadow-md)'
                                            : 'var(--shadow-sm)',
                                        border: message.sender === 'assistant' ? '1px solid var(--border-secondary)' : 'none'
                                    }, children: [_jsx("div", { className: "mb-1", dangerouslySetInnerHTML: {
                                                __html: message.sender === 'assistant' ? formatAssistantMessage(message.text) : message.text
                                            } }), _jsxs("div", { className: "d-flex justify-content-between align-items-center", children: [_jsx("small", { style: {
                                                        color: message.sender === 'user'
                                                            ? 'rgba(255, 255, 255, 0.7)'
                                                            : 'var(--text-muted)'
                                                    }, children: message.timestamp.toLocaleTimeString([], {
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    }) }), message.sender === 'assistant' && (_jsx("button", { className: "btn btn-link btn-sm p-0", onClick: () => copyToClipboard(message.text), title: "Copy analysis", style: {
                                                        fontSize: '12px',
                                                        color: 'var(--text-muted)'
                                                    }, children: _jsx("i", { className: "fas fa-copy" }) }))] })] }) }, message.id))), isLoading && (_jsx("div", { className: "mb-3 d-flex justify-content-start", children: _jsx("div", { className: "border rounded p-2", style: {
                                        background: 'var(--bg-secondary)',
                                        color: 'var(--text-primary)',
                                        border: '1px solid var(--border-secondary)'
                                    }, children: _jsxs("div", { className: "d-flex align-items-center", children: [_jsx("div", { className: "spinner-border spinner-border-sm me-2", role: "status", style: {
                                                    color: 'var(--accent-blue)'
                                                }, children: _jsx("span", { className: "visually-hidden", children: "Loading..." }) }), _jsx("small", { children: "Thinking..." })] }) }) })), _jsx("div", { ref: messagesEndRef })] }), showQuickActions && messages.length > 0 && (_jsxs("div", { className: "px-4 py-3 border-top border-bottom", style: {
                            background: 'var(--bg-muted)',
                            borderColor: 'var(--border-secondary)'
                        }, children: [_jsxs("div", { className: "d-flex align-items-center justify-content-between mb-2", children: [_jsx("small", { className: "fw-bold", style: { color: 'var(--text-muted)' }, children: "\u26A1 Quick Actions" }), _jsx("button", { className: "btn btn-link btn-sm p-0", onClick: () => setShowQuickActions(false), style: {
                                            fontSize: '10px',
                                            color: 'var(--text-muted)'
                                        }, children: _jsx("i", { className: "fas fa-times" }) })] }), _jsx("div", { className: "d-flex flex-wrap gap-2", children: quickActions.map((action, index) => (_jsx("button", { className: "btn btn-sm text-white border-0", onClick: () => handleQuickAction(action.query), disabled: isLoading, style: {
                                        fontSize: '11px',
                                        background: 'var(--accent-blue)',
                                        color: 'var(--text-inverse)',
                                        boxShadow: 'var(--shadow-sm)',
                                        transition: 'all 0.2s ease',
                                        border: 'none'
                                    }, onMouseEnter: (e) => {
                                        e.currentTarget.style.transform = 'translateY(-1px)';
                                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,123,255,0.4)';
                                    }, onMouseLeave: (e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,123,255,0.3)';
                                    }, children: action.text }, index))) })] })), _jsx("div", { className: "p-4 border-top", style: {
                            background: 'var(--bg-tertiary)',
                            borderColor: 'var(--border-secondary)'
                        }, children: _jsxs("div", { className: "input-group", children: [_jsx("textarea", { className: "form-control border-0 shadow-sm", rows: 2, value: inputText, onChange: (e) => setInputText(e.target.value), onKeyDown: handleKeyPress, placeholder: "Ask about strategies, picks, models, or specific teams...", disabled: isLoading, style: {
                                        resize: 'none',
                                        borderRadius: '20px 0 0 20px',
                                        background: 'var(--bg-tertiary)',
                                        color: 'var(--text-primary)',
                                        boxShadow: 'var(--shadow-sm)',
                                        fontSize: '15px',
                                        padding: '14px 18px',
                                        lineHeight: '1.4',
                                        border: '1px solid var(--border-secondary)'
                                    } }), _jsx("button", { className: "btn text-white border-0", onClick: sendMessage, disabled: isLoading || !inputText.trim(), style: {
                                        background: isLoading || !inputText.trim()
                                            ? 'var(--text-muted)'
                                            : 'var(--accent-green)',
                                        color: 'var(--text-inverse)',
                                        borderRadius: '0 20px 20px 0',
                                        boxShadow: 'var(--shadow-sm)',
                                        minWidth: '50px',
                                        transition: 'all 0.2s ease',
                                        border: 'none'
                                    }, children: isLoading ? (_jsx("div", { className: "spinner-border spinner-border-sm", role: "status", children: _jsx("span", { className: "visually-hidden", children: "Loading..." }) })) : (_jsx("i", { className: "fas fa-paper-plane" })) })] }) })] }))] }));
};
export default ChatWidget;
